"use client";

import type { ReactNode } from "react";
import type { CipherMode } from "@/components/tool-kit/cipher-workbench";
import { Notice } from "@/components/tool-kit/primitives";
import {
  HINT,
  LABEL,
  TABLE,
  TD,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { HillStrings } from "@/components/tools/hill-cipher";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { letterAt } from "@/lib/tool-kit/ciphers/alphabet";
import {
  type HillBlock,
  type HillConvention,
  type HillResult,
  type HillSize,
  type Invertibility,
  inverseMatrix,
  invertibility,
  type Matrix,
  type MatrixInverse,
  multiplyMatrices,
  reduceMatrix,
  suggestInvertible,
} from "@/lib/tool-kit/ciphers/hill";

const BLOCK_LIMIT = 12;
const HYPHEN_MINUS = /-/g;

/** Numbers with a true minus sign (−3, not -3), as the page's formulas write them. */
function useSignedNumber(): (value: number) => string {
  const fmt = useToolNumber();
  return (value: number) => fmt(value).replace(HYPHEN_MINUS, "−");
}

type Format = (value: number) => string;

/** A matrix between brackets, as a real table so a screen reader can walk it. */
export function MatrixView({
  matrix,
  label,
  format,
}: {
  matrix: Matrix;
  label: string;
  format: Format;
}) {
  return (
    <table
      aria-label={label}
      className="inline-table border-separate border-spacing-x-2 border-spacing-y-0.5 rounded-md border-foreground/70 border-x-2 font-mono text-foreground"
    >
      <tbody>
        {matrix.map((row, rowIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: matrix rows are positional
          <tr key={rowIndex}>
            {row.map((value, column) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: matrix cells are positional
              <td className="px-1 text-right" key={column}>
                {format(value)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Step({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{title}</h3>
      {children}
    </div>
  );
}

function tuple(values: readonly number[], format: Format): string {
  return `(${values.map(format).join(", ")})`;
}

export function BlockTable({
  blocks,
  strings,
}: {
  blocks: readonly HillBlock[];
  strings: HillStrings;
}) {
  const locale = useToolLocale();
  const fmt = useSignedNumber();
  const shown = blocks.slice(0, BLOCK_LIMIT);
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.blockColumn}
              </th>
              <th className={TH} scope="col">
                {strings.vectorColumn}
              </th>
              <th className={TH} scope="col">
                {strings.productColumn}
              </th>
              <th className={TH} scope="col">
                {strings.reducedColumn}
              </th>
              <th className={TH} scope="col">
                {strings.lettersColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((block, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: blocks are positional
              <tr key={index}>
                <td className={`${TD} font-mono`}>{block.input}</td>
                <td className={`${TD} font-mono`}>
                  {tuple(block.vector, fmt)}
                </td>
                <td className={`${TD} font-mono`}>
                  {tuple(block.product, fmt)}
                </td>
                <td className={`${TD} font-mono`}>
                  {tuple(block.reduced, fmt)}
                </td>
                <td className={`${TD} font-mono font-semibold`}>
                  {block.output}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {blocks.length > BLOCK_LIMIT ? (
        <p className={HINT}>
          {tPlural(strings.blockLimit, locale, BLOCK_LIMIT, {
            count: fmt(BLOCK_LIMIT),
          })}
        </p>
      ) : null}
    </div>
  );
}

/** Why a key does or does not have an inverse modulo 26. */
export function KeyCheck({
  check,
  strings,
}: {
  check: Invertibility;
  strings: HillStrings;
}) {
  const fmt = useSignedNumber();
  const values = {
    det: fmt(check.det),
    detMod: fmt(check.detMod),
    gcd: fmt(check.sharedFactor),
  };
  let verdict: string = strings.notInvertibleZero;
  if (check.detInverse !== null) {
    verdict = t(strings.invertible, {
      ...values,
      inverse: fmt(check.detInverse),
      product: fmt(check.detMod * check.detInverse),
    });
  } else if (check.sharedFactor === 2) {
    verdict = t(strings.notInvertibleEven, values);
  } else if (check.sharedFactor === 13) {
    verdict = t(strings.notInvertible13, values);
  }
  return (
    <div className="space-y-1 text-sm">
      <p className="font-mono text-foreground">{t(strings.detLine, values)}</p>
      <p className="font-mono text-foreground">{t(strings.gcdLine, values)}</p>
      <p className="text-foreground">{verdict}</p>
    </div>
  );
}

/** The adjugate method, step by step, then the decryption itself. */
export function InverseSteps({
  matrix,
  check,
  inverse,
  strings,
}: {
  matrix: Matrix;
  check: Invertibility;
  inverse: MatrixInverse;
  strings: HillStrings;
}) {
  const fmt = useSignedNumber();
  const identity = reduceMatrix(multiplyMatrices(matrix, inverse.inverse));
  return (
    <>
      <Step title={strings.stepDeterminant}>
        <KeyCheck check={check} strings={strings} />
      </Step>
      <Step title={strings.stepAdjugate}>
        <p className={HINT}>
          {matrix.length === 2 ? strings.adjugate2 : strings.adjugate3}
        </p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-mono">{strings.adjEquals}</span>
          <MatrixView
            format={fmt}
            label={strings.adjugateLabel}
            matrix={inverse.adjugate}
          />
          <span className="font-mono">{strings.congruent}</span>
          <MatrixView
            format={fmt}
            label={strings.adjugateModLabel}
            matrix={inverse.adjugateMod}
          />
          <span className="font-mono">{strings.modulo}</span>
        </div>
      </Step>
      <Step title={strings.stepInverse}>
        <p className={HINT}>
          {t(strings.inverseHint, { inverse: fmt(inverse.detInverse) })}
        </p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-mono">{strings.inverseEquals}</span>
          <MatrixView
            format={fmt}
            label={strings.inverseLabel}
            matrix={inverse.inverse}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-mono">{strings.identityEquals}</span>
          <MatrixView
            format={fmt}
            label={strings.identityLabel}
            matrix={identity}
          />
          <span className="font-mono">{strings.modulo}</span>
        </div>
      </Step>
    </>
  );
}

export function NoInverse({
  check,
  suggestion,
  strings,
}: {
  check: Invertibility;
  suggestion: {
    row: number;
    column: number;
    value: number;
    from: number;
  } | null;
  strings: HillStrings;
}) {
  const fmt = useSignedNumber();
  return (
    <div className="space-y-3">
      <KeyCheck check={check} strings={strings} />
      <p className={HINT}>{strings.whyInvertible}</p>
      {suggestion ? (
        <Notice>
          {t(strings.suggestion, {
            row: fmt(suggestion.row + 1),
            column: fmt(suggestion.column + 1),
            from: fmt(suggestion.from),
            to: fmt(suggestion.value),
          })}
        </Notice>
      ) : (
        <Notice>{strings.noSuggestion}</Notice>
      )}
    </div>
  );
}

export function MatrixInputs({
  size,
  entries,
  onChange,
  strings,
}: {
  size: HillSize;
  entries: readonly string[];
  onChange: (next: string[]) => void;
  strings: HillStrings;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className={LABEL}>{strings.matrix}</legend>
      <div
        className={`inline-grid gap-2 rounded-md border-foreground/70 border-x-2 px-3 py-1 ${size === 2 ? "grid-cols-2" : "grid-cols-3"}`}
      >
        {entries.map((entry, index) => {
          const row = Math.floor(index / size) + 1;
          const column = (index % size) + 1;
          const id = `hill-k-${row}-${column}`;
          const value = parseIntegerInput(entry);
          return (
            <div className="flex flex-col items-center gap-0.5" key={id}>
              <label className="sr-only" htmlFor={id}>
                {t(strings.entryLabel, { row, column })}
              </label>
              <input
                className="h-10 w-14 rounded-md border border-border bg-card text-center font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                id={id}
                inputMode="numeric"
                onChange={(event) => {
                  const next = [...entries];
                  next[index] = event.target.value;
                  onChange(next);
                }}
                value={entry}
              />
              <span
                aria-hidden="true"
                className="font-mono text-muted-foreground text-xs"
              >
                {value === null ? "·" : letterAt(value)}
              </span>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

export function HillWorking({
  mode,
  matrix,
  convention,
  result,
  strings,
}: {
  mode: CipherMode;
  matrix: Matrix;
  convention: HillConvention;
  result: HillResult | null;
  strings: HillStrings;
}) {
  const check = invertibility(matrix);
  const inverse = inverseMatrix(matrix);
  if (inverse === null || !result) {
    const fix = suggestInvertible(matrix);
    return (
      <NoInverse
        check={check}
        strings={strings}
        suggestion={fix ? { ...fix, from: matrix[fix.row][fix.column] } : null}
      />
    );
  }
  const formulas =
    convention === "column"
      ? [strings.formulaColumnEncode, strings.formulaColumnDecode]
      : [strings.formulaRowEncode, strings.formulaRowDecode];
  const blocks =
    result.blocks.length > 0 ? (
      <BlockTable blocks={result.blocks} strings={strings} />
    ) : (
      <Notice>{strings.noLetters}</Notice>
    );
  if (mode === "encode") {
    return (
      <>
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.keyHeading}</h3>
          <KeyCheck check={check} strings={strings} />
        </div>
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.encodeBlocks}</h3>
          <p className="font-mono text-foreground text-sm">{formulas[0]}</p>
          {blocks}
        </div>
      </>
    );
  }
  return (
    <>
      <InverseSteps
        check={check}
        inverse={inverse}
        matrix={matrix}
        strings={strings}
      />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.decodeBlocks}</h3>
        <p className="font-mono text-foreground text-sm">{formulas[1]}</p>
        {blocks}
      </div>
    </>
  );
}
